import { useState } from 'react';
import { AlertCircle, CheckCircle2 } from 'lucide-react';
import { buttonClass, fieldBoxClass } from './CheckoutControls';
import {
  formatCents,
  type CheckoutPlan,
  type CouponDefinition,
  type OrderSummary,
} from '@/lib/checkout/pricing';

interface OrderSummaryCardProps {
  plan: CheckoutPlan;
  order: OrderSummary;
  /** The coupon the server accepted, if any. */
  coupon: CouponDefinition | null;
  /** Checks the code with the server (Stripe). Resolves an error message, or null when applied. */
  onApplyCoupon: (code: string) => Promise<string | null>;
  onRemoveCoupon: () => void;
  /** What Stripe will charge now, once the subscription exists; replaces the estimate. */
  stripeDueNowCents?: number | null;
}

/**
 * Plan details, itemised pricing and the coupon field. Rendered alongside every
 * checkout step so pricing is always visible.
 */
export default function OrderSummaryCard({
  plan,
  order,
  coupon,
  onApplyCoupon,
  onRemoveCoupon,
  stripeDueNowCents,
}: OrderSummaryCardProps) {
  const [code, setCode] = useState('');
  const [couponError, setCouponError] = useState('');
  const [applying, setApplying] = useState(false);

  const applyCoupon = async () => {
    // Codes are case-sensitive in Stripe (MINUSACTIVATIONFEE, AcceleratorDiscount), so no upper-casing.
    const trimmed = code.trim();
    if (!trimmed) {
      setCouponError('Enter a coupon code to apply.');
      return;
    }
    setApplying(true);
    const error = await onApplyCoupon(trimmed).finally(() => setApplying(false));
    if (error) {
      setCouponError(`${error} Checkout will continue at the normal price.`);
      return;
    }
    setCouponError('');
    setCode('');
  };

  const dueNow = stripeDueNowCents ?? order.dueNowCents;

  return (
    <aside className="w-full lg:max-w-[310px]" aria-label="Order summary">
      <div className="mb-3 overflow-hidden rounded-2xl border border-[#ece6f0] bg-white">
        <div className="bg-[#ff7540] px-5 pb-4 pt-5 text-center">
          <p className="text-[17px] font-extrabold text-white">
            World<span className="font-serif italic font-normal">Via</span>
          </p>
          <p className="mt-2 text-[15px] font-semibold text-white">{plan.name}</p>
        </div>
        <p className="px-5 pt-4 text-xs leading-relaxed text-[#6b5b7d]">{plan.documentsNote}</p>
        <dl className="px-5 pb-4 pt-3">
          {order.lines.map((line) => (
            <div key={line.id} className="flex justify-between gap-3 py-1 text-[12.5px]">
              <dt className="text-[#6b5b7d]">{line.label}</dt>
              <dd
                className={`font-semibold ${line.cents < 0 ? 'text-[#0f7a63]' : 'text-[#3d1751]'}`}
              >
                {formatCents(line.cents)}
              </dd>
            </div>
          ))}
          <div className="mt-3 flex justify-between border-t border-[#ece6f0] pt-3">
            <dt className="text-sm font-bold text-[#3d1751]">Due now</dt>
            <dd className="text-sm font-bold text-[#3d1751]">{formatCents(dueNow)}</dd>
          </div>
          <p className="mt-2 text-[11px] leading-snug text-[#7a6a8c]">
            Then {formatCents(order.monthlyCents)} per month. Cancel anytime.
          </p>
        </dl>
      </div>

      <div className="rounded-2xl border border-[#ece6f0] bg-white p-4">
        {coupon ? (
          <div className="flex items-start gap-2">
            <CheckCircle2 aria-hidden="true" className="mt-0.5 h-4 w-4 flex-none text-[#02b899]" />
            <div className="min-w-0 flex-1">
              <p className="text-[12.5px] font-semibold text-[#0f7a63]">
                Coupon {coupon.code} applied
              </p>
              <p className="text-[11.5px] text-[#5c4a6e]">{coupon.label}</p>
            </div>
            <button type="button" onClick={onRemoveCoupon} className={buttonClass('smGhost')}>
              Remove
            </button>
          </div>
        ) : (
          <>
            <label htmlFor="coupon-code" className="mb-1.5 block text-xs font-semibold text-[#3d1751]">
              Coupon code
            </label>
            <div className="flex items-center gap-2">
              <input
                id="coupon-code"
                value={code}
                onChange={(event) => {
                  setCode(event.target.value);
                  setCouponError('');
                }}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') {
                    event.preventDefault();
                    void applyCoupon();
                  }
                }}
                placeholder="Coupon code"
                aria-invalid={couponError ? true : undefined}
                aria-describedby={couponError ? 'coupon-code-error' : undefined}
                className={`${fieldBoxClass(Boolean(couponError))} h-10 flex-1 text-[12.5px]`}
              />
              <button
                type="button"
                onClick={() => void applyCoupon()}
                disabled={applying}
                className={buttonClass('sm')}
              >
                {applying ? 'Checking…' : 'Apply'}
              </button>
            </div>
            {couponError ? (
              <p
                id="coupon-code-error"
                role="alert"
                className="mt-2 flex items-start gap-1 text-[11.5px] font-medium leading-snug text-[#dc2626]"
              >
                <AlertCircle aria-hidden="true" className="mt-px h-3.5 w-3.5 flex-none" />
                {couponError}
              </p>
            ) : null}
          </>
        )}
      </div>
    </aside>
  );
}
